/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ScenarioCategoryListService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to retrieve list of all scenario categories in the database.
   */
  getScenarioCategoryList(): Observable<any[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}manage-scenario-category-list`)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to remove given scenario category from the database.
   * @param category user's selected category to delete
   */
  deleteScenarioCategory(category: string): Observable<any> {
    return this.http.delete(`${this.configService.baseApiUrl}manage-scenario-category-list`,  {
      body: category
    })
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a new scenario category from the database.
   * @param category user inputted category to add
   */
  addScenarioCategory(category: string): Observable<any> {
    return this.http.post(`${this.configService.baseApiUrl}manage-scenario-category-list`, category)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a put request to edit an existing scenario category name in the database.
   * @param oldCategory existing value that will be modified.
   * @param newCategory New value to replace existing value.
   */
  editScenarioCategory(oldCategory: string, newCategory: string): Observable<any> {
    const params = new HttpParams().set('newCategory', newCategory).set('oldCategory', oldCategory);
    return this.http.put(`${this.configService.baseApiUrl}manage-scenario-category-list`, null, {params})
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        } else {
          errorMessage = 'Error loading scenario category list.';
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

}

